import { useEffect, useState, type FormEvent } from 'react';
import {
  systemSettings, type AdminRecipient, type AppUpdateEntry, type MailPolicy,
} from './system.api';
import type { PlatformSettings } from '@todo/shared';
import {
  Panel, ErrorNote, Field, inputClass, buttonClass, tableHeadClass,
} from './SystemLayout';

/**
 * The two GLOBAL settings, moved here from the tenant Settings page.
 *
 * Both govern every organisation at once: mail_policy is a single row
 * (the table has CHECK (id = 1)) and app_update_config is one row per
 * mobile platform. While they sat behind a tenant SUPER_ADMIN guard, any
 * one customer's administrator could pause mail for every tenant, or push
 * a force-update to every customer's mobile users. The tenant routes now
 * refuse writes and point here.
 *
 * "Updated by" is blank for anything saved from the console: the column
 * references users.id and a platform operator has no users row. The Audit
 * tab is the record of who changed what.
 */
export default function PlatformSettingsPage() {
  return (
    <div className="space-y-5">
      <PortalUrlCard />
      <MailPolicyCard />
      <AppUpdateCard />
    </div>
  );
}

// ------------------------------------------------------------ portal URL --

/**
 * The base URL every link in an outgoing email is built from.
 *
 * It was CLIENT_URL, an environment variable - so "our portal moved" was
 * a deployment change made by whoever holds app-settings access. That is
 * the wrong person and the wrong tool.
 *
 * The scope is narrow and the card says so: this moves the LINKS, not the
 * app, and not what the API's CORS will accept. Both of those still come
 * from the environment at boot, which is why the environment value is
 * shown beside the field - a mismatch is the one way to get this wrong
 * and not find out until a recipient clicks a dead link.
 */
function PortalUrlCard() {
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    systemSettings.platformSettings()
      .then((p) => { setSettings(p); setValue(p.appBaseUrl ?? ''); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load the portal URL'));
  }, []);

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const saved = await systemSettings.savePlatformSettings({
        appBaseUrl: value.trim() || null,
      });
      setSettings(saved);
      setValue(saved.appBaseUrl ?? '');
      setFlash(
        saved.appBaseUrl
          ? `Email links now point at ${saved.appBaseUrl}.`
          : 'Cleared - email links use the server setting again.',
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  const mismatch =
    settings?.appBaseUrl != null && settings.appBaseUrl !== settings.envBaseUrl;

  return (
    <Panel title="Portal URL in emails">
      <p className="mb-3 text-sm text-slate-500">
        Every link we email - sign-in, signature requests, meeting invites -
        is built from this. Leave it empty to use the server's own setting.
        It does <strong>not</strong> move the portal or change which origin
        the API accepts; both of those stay in the server configuration.
      </p>

      <ErrorNote message={error} />
      {flash && (
        <p className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {flash}
        </p>
      )}

      <form onSubmit={(e) => void save(e)} className="space-y-3">
        <Field
          label="Base URL"
          hint="Scheme and host, no trailing slash - https://todo.example.com"
        >
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={settings?.envBaseUrl ?? 'https://todo.example.com'}
            className={inputClass}
          />
        </Field>

        {settings && (
          <dl className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
            <div className="flex justify-between gap-3 py-0.5">
              <dt className="text-slate-500">Server setting (CLIENT_URL)</dt>
              <dd className="font-mono text-slate-700">{settings.envBaseUrl}</dd>
            </div>
            <div className="flex justify-between gap-3 py-0.5">
              <dt className="text-slate-500">Links currently use</dt>
              <dd className="font-mono font-semibold text-slate-900">
                {settings.effectiveBaseUrl}
              </dd>
            </div>
          </dl>
        )}

        {/* The one real footgun: links that point somewhere the app is not
            served from, or that the API will not talk to. */}
        {mismatch && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            This differs from the server setting. That is fine if the portal
            really is served there - but if it is not, every emailed link
            will be dead, and the API may refuse requests from that origin.
          </p>
        )}

        <button type="submit" disabled={busy} className={buttonClass}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </form>
    </Panel>
  );
}

// ------------------------------------------------------------ mail policy --

function MailPolicyCard() {
  const [policy, setPolicy] = useState<MailPolicy | null>(null);
  const [admins, setAdmins] = useState<AdminRecipient[]>([]);
  /** Per-org mutes, indexed as "orgId|email" for O(1) lookup while rendering. */
  const [orgBlocks, setOrgBlocks] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [savingRow, setSavingRow] = useState<string | null>(null);
  const [showStaging, setShowStaging] = useState(false);

  const [paused, setPaused] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [redirectTo, setRedirectTo] = useState('');
  const [allowlist, setAllowlist] = useState('');
  /** The GLOBAL blocklist - addresses switched off in every organisation. */
  const [globalBlocked, setGlobalBlocked] = useState<Set<string>>(new Set());

  const key = (orgId: string, email: string) => `${orgId}|${email.toLowerCase()}`;

  async function load() {
    try {
      const [p, people, blocks] = await Promise.all([
        systemSettings.mailPolicy(),
        systemSettings.adminRecipients(),
        systemSettings.orgBlocks(),
      ]);
      setPolicy(p);
      setAdmins(people);
      setPaused(p.paused);
      setPauseReason(p.pauseReason ?? '');
      setRedirectTo(p.redirectTo ?? '');
      setAllowlist(p.allowlist.join('\n'));
      setGlobalBlocked(new Set(p.blocklist.map((b) => b.toLowerCase())));
      setOrgBlocks(new Set(blocks.map((b) => key(b.orgId, b.email))));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the mail policy');
    }
  }

  useEffect(() => { void load(); }, []);

  /**
   * Tick = this person receives mail FOR THIS ORGANISATION.
   *
   * Per-org, because one person can administer several organisations on
   * ONE email address. Switching them off here mutes that organisation's
   * mail and leaves their other organisations alone - which is the thing
   * the global list below cannot express.
   */
  async function toggleOrg(person: AdminRecipient, send: boolean) {
    const k = key(person.orgId, person.email);
    setSavingRow(k);
    setError(null);
    try {
      const rows = await systemSettings.setOrgBlock(
        person.orgId, person.email, !send, null,
      );
      setOrgBlocks(new Set(rows.map((b) => key(b.orgId, b.email))));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change that');
    } finally {
      setSavingRow(null);
    }
  }

  /** The blunt instrument: this address, in every organisation. */
  async function toggleGlobal(email: string, block: boolean) {
    const address = email.toLowerCase();
    const next = new Set(globalBlocked);
    if (block) next.add(address); else next.delete(address);

    setSavingRow(address);
    setError(null);
    try {
      const saved = await systemSettings.saveMailPolicy({
        paused,
        pauseReason: pauseReason.trim() || null,
        redirectTo: redirectTo.trim() || null,
        allowlist: allowlist.split('\n').map((l) => l.trim()).filter(Boolean),
        blocklist: [...next],
      });
      setPolicy(saved);
      setGlobalBlocked(new Set(saved.blocklist.map((b) => b.toLowerCase())));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change that');
    } finally {
      setSavingRow(null);
    }
  }

  async function savePolicy(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const saved = await systemSettings.saveMailPolicy({
        paused,
        pauseReason: pauseReason.trim() || null,
        redirectTo: redirectTo.trim() || null,
        allowlist: allowlist.split('\n').map((l) => l.trim()).filter(Boolean),
        blocklist: [...globalBlocked],
      });
      setPolicy(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the mail policy');
    } finally {
      setBusy(false);
    }
  }

  const byOrg = admins.reduce<Record<string, AdminRecipient[]>>((acc, a) => {
    (acc[a.orgName] ??= []).push(a);
    return acc;
  }, {});
  const usingAllowlist = allowlist.trim().length > 0;

  return (
    <Panel title="Outgoing mail">
      <ErrorNote message={error} />

      {policy && !policy.mailConfigured && (
        <p className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          SMTP is not configured on this server, so nothing is sent whatever this policy
          says. Set SMTP_HOST and MAIL_FROM.
        </p>
      )}

      {policy?.active && (
        <p className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Mail is paused right now. Blocked messages are dropped and logged, never queued.
        </p>
      )}

      <form onSubmit={(e) => void savePolicy(e)} className="space-y-6">
        {/* --- 1. the hiatus --- */}
        <label className="flex items-start gap-2">
          <input type="checkbox" checked={paused} onChange={(e) => setPaused(e.target.checked)}
                 className="mt-1" />
          <span>
            <span className="block text-sm font-medium text-slate-700">Pause all outgoing mail</span>
            <span className="block text-xs text-slate-500">
              Every organisation, and it beats everything below. Suppressed, not queued.
            </span>
          </span>
        </label>

        {paused && (
          <Field label="Pause reason" hint="Recorded in the log. Recipients never see it.">
            <input value={pauseReason} onChange={(e) => setPauseReason(e.target.value)}
                   className={inputClass} />
          </Field>
        )}
      </form>

      {/* --- 2. per-person, per-organisation. Saves on click, outside the
              form, because each tick is its own API call. --- */}
      <div className="mt-6">
        <h3 className="text-sm font-medium text-slate-700">Who receives mail</h3>
        <p className="mb-3 mt-1 text-xs text-slate-500">
          Administrators, grouped by organisation. <strong>A tick is per organisation</strong> -
          the same person appears once under each one they administer, so you can stop
          their mail for a single organisation and leave the others alone. Saves
          immediately.
        </p>

        {admins.length === 0 ? (
          <p className="text-sm text-slate-500">No administrators with an email address.</p>
        ) : (
          <div className="max-h-80 overflow-y-auto rounded-md border border-slate-200">
            {Object.entries(byOrg).map(([orgName, people]) => (
              <div key={orgName}>
                <p className="sticky top-0 border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {orgName}
                </p>
                {people.map((person) => {
                  const k = key(person.orgId, person.email);
                  const mutedHere = orgBlocks.has(k);
                  const mutedEverywhere = globalBlocked.has(person.email.toLowerCase());
                  const saving = savingRow === k || savingRow === person.email.toLowerCase();
                  return (
                    <div key={person.userId}
                      className="flex items-center gap-3 border-b border-slate-100 px-3 py-2 last:border-0 hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={!mutedHere && !mutedEverywhere}
                        /* A global block wins, so the per-org tick is
                           meaningless while it is on - disabled rather
                           than silently ignored. */
                        disabled={mutedEverywhere || saving}
                        onChange={(e) => void toggleOrg(person, e.target.checked)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className={mutedHere || mutedEverywhere
                          ? 'text-sm text-slate-400 line-through'
                          : 'text-sm text-slate-900'}>
                          {person.displayName}
                        </span>
                        <span className="ml-2 text-xs text-slate-500">{person.email}</span>
                        {mutedEverywhere && (
                          <span className="ml-2 text-xs font-medium text-red-600">
                            blocked everywhere
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">
                        {person.roleSystemKey === 'SUPER_ADMIN' ? 'Super Admin' : 'Admin'}
                      </span>
                      <button type="button" disabled={saving}
                        onClick={() => void toggleGlobal(person.email, !mutedEverywhere)}
                        title="Stop this address receiving mail from EVERY organisation"
                        className="shrink-0 text-xs font-medium text-slate-500 underline-offset-2 hover:text-red-700 hover:underline disabled:opacity-40">
                        {mutedEverywhere ? 'Unblock everywhere' : 'Block everywhere'}
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* Addresses on the global list that belong to no administrator -
            typed by hand, or someone since removed. Shown so saving the
            form cannot silently drop them. */}
        {[...globalBlocked].filter(
          (b) => !admins.some((a) => a.email.toLowerCase() === b),
        ).length > 0 && (
          <p className="mt-2 text-xs text-slate-500">
            Also blocked everywhere, and not an administrator:{' '}
            {[...globalBlocked]
              .filter((b) => !admins.some((a) => a.email.toLowerCase() === b))
              .join(', ')}
          </p>
        )}
      </div>

      {/* --- 3. the staging filter, folded away --- */}
      <form onSubmit={(e) => void savePolicy(e)} className="mt-6 space-y-6">
        <div className="rounded-md border border-slate-200">
          <button type="button" onClick={() => setShowStaging((v) => !v)}
            className="flex w-full items-center justify-between px-3 py-2 text-left">
            <span className="text-sm font-medium text-slate-700">
              Staging filter {usingAllowlist && (
                <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700">
                  in use
                </span>
              )}
            </span>
            <span className="text-xs text-slate-500">{showStaging ? 'Hide' : 'Show'}</span>
          </button>

          {showStaging && (
            <div className="space-y-4 border-t border-slate-200 p-3">
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                For pointing a TEST server at yourself - not a per-person switch, which is
                the list above. <strong>Empty means everyone receives mail.</strong> Put one
                address here and every address <em>not</em> listed stops receiving mail -
                every employee of every organisation - and goes to the redirect instead
                (or is dropped if there is none).
              </p>

              <Field label="Allowlist - one address or @domain per line">
                <textarea value={allowlist} onChange={(e) => setAllowlist(e.target.value)}
                          rows={3} className={`${inputClass} font-mono`} />
              </Field>

              <Field
                label="Redirect everything else to"
                hint="Only used when the allowlist is in use. The real recipient is prefixed to the subject. A blocked or muted address is NOT redirected here - switched off means switched off."
              >
                <input type="email" value={redirectTo} onChange={(e) => setRedirectTo(e.target.value)}
                       className={inputClass} placeholder="you@example.com" />
              </Field>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button type="submit" disabled={busy} className={buttonClass}>
            {busy ? 'Saving...' : 'Save pause and staging filter'}
          </button>
          {policy?.mailFrom && (
            <span className="text-xs text-slate-500">Sending as {policy.mailFrom}</span>
          )}
        </div>
      </form>
    </Panel>
  );
}

// ------------------------------------------------------ mobile force-update --

function AppUpdateCard() {
  const [entries, setEntries] = useState<AppUpdateEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await systemSettings.appUpdate();
        if (!cancelled) setEntries(rows);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load the app settings');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <Panel title="Mobile force-update">
      <ErrorNote message={error} />
      <p className="mb-4 text-xs text-slate-500">
        Served to every organisation's mobile app by /api/app-version. A build below the
        minimum version is blocked until the user updates, so raising it locks out anyone
        who has not - set it deliberately.
      </p>

      <table className="w-full text-sm">
        <thead>
          <tr className={tableHeadClass}>
            <th className="py-2 font-medium">Platform</th>
            <th className="py-2 font-medium">Minimum</th>
            <th className="py-2 font-medium">Latest</th>
            <th className="py-2 font-medium">Source</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <PlatformRow key={entry.platform} entry={entry}
              onSaved={(saved) => setEntries((cur) =>
                cur.map((e) => (e.platform === saved.platform ? saved : e)))}
              onError={setError} />
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

function PlatformRow({ entry, onSaved, onError }: {
  entry: AppUpdateEntry;
  onSaved: (entry: AppUpdateEntry) => void;
  onError: (message: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [minVersion, setMinVersion] = useState(entry.minVersion);
  const [latestVersion, setLatestVersion] = useState(entry.latestVersion);
  const [storeUrl, setStoreUrl] = useState(entry.storeUrl);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    onError(null);
    try {
      onSaved(await systemSettings.saveAppUpdate(entry.platform, {
        minVersion, latestVersion, storeUrl,
      }));
      setEditing(false);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <tr className="border-b border-slate-100 last:border-0">
        <td colSpan={4} className="py-3">
          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_2fr_auto]">
            <Field label="Minimum">
              <input value={minVersion} onChange={(e) => setMinVersion(e.target.value)}
                     placeholder="1.5.0" className={inputClass} />
            </Field>
            <Field label="Latest">
              <input value={latestVersion} onChange={(e) => setLatestVersion(e.target.value)}
                     placeholder="1.6.0" className={inputClass} />
            </Field>
            <Field label="Store URL">
              <input value={storeUrl} onChange={(e) => setStoreUrl(e.target.value)} className={inputClass} />
            </Field>
            <div className="flex items-end gap-2">
              <button type="button" disabled={busy} onClick={() => void save()} className={buttonClass}>
                {busy ? 'Saving...' : 'Save'}
              </button>
              <button type="button" onClick={() => setEditing(false)}
                      className="text-xs font-medium text-slate-500 hover:text-slate-900">
                Cancel
              </button>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-slate-100 last:border-0">
      <td className="py-2.5 capitalize text-slate-900">{entry.platform}</td>
      <td className="py-2.5 text-slate-500">{entry.minVersion || '-'}</td>
      <td className="py-2.5 text-slate-500">{entry.latestVersion || '-'}</td>
      <td className="py-2.5">
        {/* 'env' means no row exists yet and the APP_UPDATE_* environment
            values are in force; 'none' means neither, so the app gets a 503. */}
        <span className="text-xs text-slate-500">
          {entry.source === 'db' ? 'Saved' : entry.source === 'env' ? 'From server configuration' : 'Not set'}
        </span>
        <button type="button" onClick={() => setEditing(true)}
                className="ml-3 text-xs font-medium text-brand-600 underline-offset-2 hover:underline">
          Edit
        </button>
      </td>
    </tr>
  );
}
